import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.object.assign.js";
var _excluded = ["id", "className", "validationError", "errorMessage", "labelClassName", "label", "labelControl", "small", "isFormItem", "optional", "optionalIndicatorText", "required", "requiredMessage", "hideLabel", "title", "hint", "helpMessage", "helpLabel", "helpAriaLabel"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import "core-js/modules/es.string.small.js";
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/* eslint-disable jsx-a11y/label-has-associated-control */
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2017,2022. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InputErrorMessage from './InputErrorMessage';
import { uniqueIdForComponent } from '../../../util';
import Label from './Label';

/**
 * Represents a Checkbox displayed to the user
 */
var propTypes = {
  /** An unique ID is needed in order to support keyboard navigation and ARIA support.
   * This ID is added to the input element */
  id: PropTypes.string,
  /** class names to be added to the outer container of the Checkbox. */
  className: PropTypes.string,
  /** class names to be added to the label element. */
  labelClassName: PropTypes.string,
  /** disables the Checkbox and prevents clicking it. */
  disabled: PropTypes.bool,
  /** label for the Checkbox, string or html element */
  label: PropTypes.node.isRequired,
  /**  control element for the input field label */
  labelControl: PropTypes.node,
  /** small variant for the Checkbox. */
  small: PropTypes.bool,
  /** specifies if this Checkbox should be rendered as inside a form item element. */
  isFormItem: PropTypes.bool,
  /** specifies that the Checkbox is in an validation error state */
  validationError: PropTypes.bool,
  /** Message to display when the Checkbox is in an validation error state.
   * When this is present, also visually highlights the component as in error.
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /** bool value to indicate if the field is optional field */
  optional: PropTypes.bool,
  /** text to indicate a field is optional */
  optionalIndicatorText: PropTypes.string,
  /** specifies if the Checkbox is a required field. */
  required: PropTypes.bool,
  /** message to display when Checkbox is required. */
  requiredMessage: PropTypes.string,
  /** called when the Checkbox is changed. */
  onChange: PropTypes.func,
  /** This event fires when the Checkbox is focused. */
  onFocus: PropTypes.func,
  /** indicates that the Checkbox label is hidden. */
  hideLabel: PropTypes.bool,
  /** specifies extra information for the Checkbox.  */
  title: PropTypes.string,
  /** hint text */
  hint: PropTypes.node,
  /** node to be used as the help message */
  helpMessage: PropTypes.node,
  /** help toggle button label */
  helpLabel: PropTypes.string,
  /** used by screen readers to describe the help toggle button */
  helpAriaLabel: PropTypes.string
};
var defaultProps = {
  id: undefined,
  className: undefined,
  labelClassName: undefined,
  labelControl: undefined,
  disabled: false,
  small: false,
  isFormItem: false,
  validationError: false,
  errorMessage: undefined,
  optional: false,
  optionalIndicatorText: undefined,
  required: false,
  requiredMessage: undefined,
  onChange: function onChange() {},
  onFocus: function onFocus() {},
  hideLabel: false,
  title: undefined,
  hint: undefined,
  helpMessage: undefined,
  helpLabel: 'Help',
  helpAriaLabel: 'Help'
};
var Checkbox = function Checkbox(_ref) {
  var id = _ref.id,
    className = _ref.className,
    validationError = _ref.validationError,
    errorMessage = _ref.errorMessage,
    labelClassName = _ref.labelClassName,
    label = _ref.label,
    labelControl = _ref.labelControl,
    small = _ref.small,
    isFormItem = _ref.isFormItem,
    optional = _ref.optional,
    optionalIndicatorText = _ref.optionalIndicatorText,
    required = _ref.required,
    requiredMessage = _ref.requiredMessage,
    hideLabel = _ref.hideLabel,
    title = _ref.title,
    hint = _ref.hint,
    helpMessage = _ref.helpMessage,
    helpLabel = _ref.helpLabel,
    helpAriaLabel = _ref.helpAriaLabel,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var componentId = id || uniqueIdForComponent();
  var formItemStyle = classNames('wds-c-form-item', {
    'wds-c-input-error': validationError
  });
  var checkboxStyle = classNames(className, 'wds-c-checkbox', {
    'wds-c-checkbox--small': small,
    'wds-c-checkbox--small--hide-label': hideLabel
  });
  var labelId = "".concat(componentId, "-label");
  var hintId = "".concat(componentId, "-hint");
  var errorId = "".concat(componentId, "-error");
  var labelledBy;
  if (validationError) {
    labelledBy = errorId;
  }
  var checkboxElement = /*#__PURE__*/React.createElement("input", _extends({
    id: componentId,
    type: "checkbox"
  }, hint && {
    'aria-describedby': hintId
  }, labelledBy && {
    'aria-labelledby': labelledBy
  }, hideLabel && {
    'aria-label': label
  }, {
    required: required
  }, otherProps));
  var labelElement = /*#__PURE__*/React.createElement(React.Fragment, null, checkboxElement, /*#__PURE__*/React.createElement(Label, {
    id: labelId,
    hint: hint,
    required: required,
    title: title,
    className: labelClassName,
    htmlFor: componentId,
    helpMessage: helpMessage,
    helpLabel: helpLabel,
    helpAriaLabel: helpAriaLabel,
    optional: optional,
    optionalIndicatorText: optionalIndicatorText,
    requiredIndicatorText: requiredMessage
  }, label));
  var checkboxElements = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: checkboxStyle
  }, labelElement, labelControl), validationError && /*#__PURE__*/React.createElement(InputErrorMessage, {
    id: errorId
  }, errorMessage));
  return isFormItem ? /*#__PURE__*/React.createElement("div", {
    className: formItemStyle
  }, checkboxElements, " ") : checkboxElements;
};
Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;
export default Checkbox;